import { useMemo } from 'react'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  SheduleTableProps,
  SchedulerPackagingRoutesRowType,
} from './Scheduler.types'
import { Box } from '@mui/material'
import { GridCellParams } from '@mui/x-data-grid-premium'
import { DropZone } from 'Features/SystemOfRecords/Tabs/DropZone'
import { cellText, routeKitchens, transferSlots } from './tankRoutes'

const dataConfig = {
  required: [
    'baseProductCode',
    'kitchen',
    'kitchenRunRate',
    'lineRunRate1',
    'packingLine1',
    'pr1',
    'productCode',
    'maxRunTime',
  ],
  optional: [
    'baseProductName',
    'lineRunRate2',
    'lineRunRate3',
    'lineRunRate4',
    'packingLine2',
    'packingLine3',
    'packingLine4',
    'pr2',
    'pr3',
    'pr4',
    'productName',
    'prepTime',
    'inputPrepTime',
    'minRunTime',
    'preferredRunTime',
    'maxBatches',
    // Holding tanks (V1). All nullable and absent-tolerant: a route that sets
    // none of them is an ordinary single-segment route. The tank itself goes
    // in the Kitchen or a Line column, so it needs no column of its own.
    // inputTankId / outputTankId / inputProductCode are deliberately NOT
    // offered for upload any more; the engine still reads them on old data.
    'routeId',
    'segmentIndex',
    'maturationHours',
  ],
}
/** The nullable holding-tank columns, in grid order. */
const TANK_FIELDS = ['routeId', 'segmentIndex', 'maturationHours'] as const
// The pre-v7.152.0 tank columns (inputTankId / outputTankId /
// inputProductCode) are added only while `showLegacyTankColumns` says data
// holds them — see the spread at the end of `columns`.

export const SchedulerPackagingRoutesTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
  showTankColumns = true,
  tankNames = [],
  showLegacyTankColumns = false,
}: SheduleTableProps<SchedulerPackagingRoutesRowType> & {
  /** Whether the holding-tank columns (Route, Segment, Maturation) are SHOWN
   *  by default.
   *
   *  They are meaningful only to a plant with a vessel, and this grid is the
   *  widest in the app — a 263-row route table on a plant that has no tank and
   *  never will was carrying permanently empty columns. They are always in the
   *  grid, though, so the Columns selector can show them: until 2026-10-30
   *  this flag removed them outright, and a planner setting up a plant's first
   *  tank chain had no way to reach them.
   *
   *  Defaults TRUE so the decision is the caller's and no other call site
   *  changes behaviour. `SchedulePackagingRoutes.view` supplies the real
   *  answer; see the comment there for why "no tank declared" is not the same
   *  question as "no route mentions a tank".
   */
  showTankColumns?: boolean
  /** Declared tank ids (the tanks table). A name in the Kitchen or a Line
   *  cell that matches one IS that tank, which is how a segment says it fills
   *  (Line) or drains (Kitchen) the vessel. Empty when no tank is declared. */
  tankNames?: string[]
  /** Render the legacy inputTankId / outputTankId / inputProductCode columns,
   *  read-only. The caller sets this only when some row still holds a value. */
  showLegacyTankColumns?: boolean
}) => {
  const tanks = useMemo(() => new Set(tankNames), [tankNames])
  const isTank = (v: unknown) => {
    const t = cellText(v)
    return t != null && tanks.has(t)
  }
  // Which kitchens each route runs on: a transfer row (`TANK02 -> PKN12`)
  // names one of them in a Line slot.
  const kitchensByRoute = useMemo(
    () => routeKitchens(data ?? [], tanks),
    [data, tanks]
  )
  // A fill runs at the kitchen's rate, so a Line slot holding a tank takes no
  // Line Run Rate or PR Factor; nor does a transfer's second kitchen, which
  // draws at its own rate on its own row. Locking those cells says so, rather
  // than letting a planner type a rate the engine ignores.
  const isCellEditable = (params: GridCellParams) => {
    const m = /^(lineRunRate|pr)([1-4])$/.exec(params.field)
    if (!m) return params.colDef.editable ?? false
    const row = params.row as SchedulerPackagingRoutesRowType
    if (transferSlots(row, tanks, kitchensByRoute).includes(Number(m[2])))
      return false
    return !isTank(row[`packingLine${m[2]}` as keyof typeof row])
  }
  const tankCellClass = (params: GridCellParams) =>
    isTank(params.value) ? 'tank-cell' : ''
  const tankHint =
    tankNames.length > 0
      ? ` Declared tanks: ${tankNames.join(', ')}.`
      : ' No tank is declared on this schedule.'

  // Handle data updates for BaseTable
  const handleDataUpdate = (
    changedRows: Record<number, SchedulerPackagingRoutesRowType>
  ) => {
    updateTableData(changedRows)
  }

  //Example data:
  //   {
  //     "baseProductCode": "901615151",
  //     "baseProductName": "HP MCD PANCAKE SYRUP NAT",
  //     "id": 0,
  //     "kitchen": "1",
  //     "kitchenRunRate": null,
  //     "lineRunRate1": "462",
  //     "lineRunRate2": null,
  //     "lineRunRate3": null,
  //     "lineRunRate4": null,
  //     "packingLine1": "DPL04",
  //     "packingLine2": "N/A",
  //     "packingLine3": "N/A",
  //     "packingLine4": "N/A",
  //     "pr": "47.00%",
  //     "productCode": "901621135",
  //     "productName": "FMCD PANCAKE SYRUP 168X40G​"
  // }
  const columns: ExtendedGridColDef[] = useMemo(
    () =>
      [
        {
          field: 'baseProductCode',
          headerName: 'Input Product Code',
          type: 'string',
          flex: 1,
        },
        {
          field: 'baseProductName',
          headerName: 'Input Product Description',
          type: 'string',
          flex: 1,
        },
        {
          field: 'kitchen',
          headerName: 'Kitchen',
          flex: 1,
          editable: true,
          type: 'string',
          cellClassName: tankCellClass,
          description:
            'A kitchen, or a declared tank: a tank here means this segment DRAWS from it (Tank → Lines), and Kitchen Run Rate is its drain rate.' +
            tankHint,
        },
        {
          field: 'kitchenRunRate',
          headerName: 'Kitchen Run Rate',
          flex: 1,
          editable: true,
          type: 'number',
        },
        {
          // Stored as `productCode` so every existing route keeps its key,
          // but read as a PRODUCT GROUP code: a product takes the routes of
          // the group its Product Master row names, and a product with no
          // group is its own group, so a route keyed by its code still
          // applies to it (engine: `product_groups.rs`).
          field: 'productCode',
          headerName: 'Product Group code',
          type: 'string',
          flex: 1,
          description:
            'The product group this route is for. Every product whose Product Group (Product Master) is this code uses the route. A product with no Product Group is its own group, so its product code works here as before.',
        },
        {
          field: 'productName',
          headerName: 'Product Name',
          type: 'string',
          flex: 1,
        },
        {
          field: 'lineRunRate1',
          headerName: 'Line Run Rate 1',
          flex: 1,
          editable: true,
          type: 'number',
        },
        {
          field: 'lineRunRate2',
          headerName: 'Line Run Rate 2',
          flex: 1,
          editable: true,
          type: 'number',
        },
        {
          field: 'lineRunRate3',
          headerName: 'Line Run Rate 3',
          flex: 1,
          editable: true,
          type: 'number',
        },
        {
          field: 'lineRunRate4',
          headerName: 'Line Run Rate 4',
          flex: 1,
          editable: true,
          type: 'number',
        },
        {
          field: 'packingLine1',
          headerName: 'Line Group 1',
          type: 'string',
          flex: 1,
          editable: true,
          cellClassName: tankCellClass,
          description:
            'A line group (the run packs on whichever of its lines can start earliest; a line with no Resource group code is its own group), or a declared tank: a tank here means this segment FILLS it (produce to stock). On a row whose Kitchen is a tank, a kitchen of the same route here means the tank is TRANSFERRED into that kitchen (Tank → Kitchen2). Neither takes a run rate or PR.' +
            tankHint,
        },
        {
          field: 'packingLine2',
          headerName: 'Line Group 2',
          type: 'string',
          flex: 1,
          editable: true,
          cellClassName: tankCellClass,
          description:
            'A line group (the run packs on whichever of its lines can start earliest; a line with no Resource group code is its own group), or a declared tank: a tank here means this segment FILLS it (produce to stock). On a row whose Kitchen is a tank, a kitchen of the same route here means the tank is TRANSFERRED into that kitchen (Tank → Kitchen2). Neither takes a run rate or PR.' +
            tankHint,
        },
        {
          field: 'packingLine3',
          headerName: 'Line Group 3',
          type: 'string',
          flex: 1,
          editable: true,
          cellClassName: tankCellClass,
          description:
            'A line group (the run packs on whichever of its lines can start earliest; a line with no Resource group code is its own group), or a declared tank: a tank here means this segment FILLS it (produce to stock). On a row whose Kitchen is a tank, a kitchen of the same route here means the tank is TRANSFERRED into that kitchen (Tank → Kitchen2). Neither takes a run rate or PR.' +
            tankHint,
        },
        {
          field: 'packingLine4',
          headerName: 'Line Group 4',
          type: 'string',
          flex: 1,
          editable: true,
          cellClassName: tankCellClass,
          description:
            'A line group (the run packs on whichever of its lines can start earliest; a line with no Resource group code is its own group), or a declared tank: a tank here means this segment FILLS it (produce to stock). On a row whose Kitchen is a tank, a kitchen of the same route here means the tank is TRANSFERRED into that kitchen (Tank → Kitchen2). Neither takes a run rate or PR.' +
            tankHint,
        },
        {
          field: 'pr1',
          headerName: 'PR Factor 1',
          type: 'string',
          flex: 1,
          editable: true,
        },
        {
          field: 'pr2',
          headerName: 'PR Factor 2',
          type: 'string',
          flex: 1,
          editable: true,
        },
        {
          field: 'pr3',
          headerName: 'PR Factor 3',
          type: 'string',
          flex: 1,
          editable: true,
        },
        {
          field: 'pr4',
          headerName: 'PR Factor 4',
          type: 'string',
          flex: 1,
          editable: true,
        },
        {
          field: 'prepTime',
          headerName: 'Off-set',
          type: 'number',
          flex: 1,
          editable: true,
        },
        {
          field: 'inputPrepTime',
          headerName: 'Prep Time',
          type: 'number',
          flex: 1,
          editable: true,
        },
        {
          // The HARD floor — a batch may never run shorter than this. Unchanged
          // meaning: this column was already a feasibility constraint before the
          // preferred tier existed.
          headerName: 'Min Run Time',
          field: 'minRunTime',
          type: 'number',
          flex: 1,
          editable: true,
        },
        {
          // The PREFERRED length — soft. The scheduler may run a batch shorter
          // than this where that produces a better schedule, paying a penalty in
          // the throughput objective, but never shorter than Min Run Time.
          //
          // Blank or 0 means this route expresses no preference and pays no
          // penalty however short its batch runs — it does NOT inherit Min Run
          // Time. A value below Min Run Time is clamped up to it by the engine,
          // since the floor already guarantees more than the preference asks.
          headerName: 'Preferred Run Time',
          field: 'preferredRunTime',
          type: 'number',
          flex: 1,
          editable: true,
        },
        {
          headerName: 'Max Run Time',
          field: 'maxRunTime',
          type: 'number',
          flex: 1,
          editable: true,
        },
        {
          headerName: 'Max Batches',
          field: 'maxBatches',
          type: 'number',
          flex: 1,
          editable: true,
        },
        // ── Holding tanks: this table is now a SEGMENT table ────────────────
        // Its rows always were a segment; they simply never chained. The tank
        // is named in the Kitchen or a Line cell; the columns below only say
        // which segments belong together and how long the batch is held.
        {
          headerName: 'Route',
          field: 'routeId',
          type: 'string',
          flex: 1,
          editable: true,
          description:
            'Groups the segments of one chain, one row per step. Needed for Tank → Kitchen2 → Lines, entered as three rows on one route: Kitchen → Tank (fill), Tank → Kitchen2 (transfer), Kitchen2 → Lines. Blank on an ordinary route.',
        },
        {
          headerName: 'Segment',
          field: 'segmentIndex',
          type: 'number',
          flex: 1,
          editable: true,
          description: 'Position in that chain: 1, 2, …',
        },
        {
          headerName: 'Maturation (h)',
          field: 'maturationHours',
          type: 'number',
          flex: 1,
          editable: true,
          description:
            'How long the batch must be held in the tank before it may be drained, in HOURS. Sits on the segment that FILLS the tank.',
        },
        // ── Legacy tank columns (read-only) ─────────────────────────────────
        // Schedules created before v7.152.0 carry them and the engine still
        // obeys them, so hiding them would leave a live setting invisible.
        // They are not editable: the fix is to move the tank into the
        // Kitchen / Line cell (scripts/convert_tank_route_columns.py does it
        // for a whole table), not to keep maintaining the old shape.
        ...(showLegacyTankColumns
          ? [
              {
                headerName: 'Input Tank (legacy)',
                field: 'inputTankId',
                type: 'string',
                flex: 1,
                editable: false,
                description:
                  'Old layout, still read by the scheduler. Put the tank in the Kitchen column instead (Tank → Lines), or give a Tank → Kitchen2 row the Route of its fill row.',
              },
              {
                headerName: 'Output Tank (legacy)',
                field: 'outputTankId',
                type: 'string',
                flex: 1,
                editable: false,
                description:
                  'Old layout, still read by the scheduler. Put the tank in an empty Line column instead.',
              },
              {
                headerName: 'Input Product Code (legacy)',
                field: 'inputProductCode',
                type: 'string',
                flex: 1,
                editable: false,
                description:
                  'Old layout. Never read by the scheduler; safe to leave.',
              },
            ]
          : []),
      ] as ExtendedGridColDef[],
    // tankCellClass / tankHint derive from `tanks` and `tankNames`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, showTankColumns, showLegacyTankColumns, tanks]
  )

  return (
    <Box>
      <Box flexDirection="row">
        <DropZone
          uploadFile={uploadScheduleData}
          isUploading={isUploading}
          columns={columns}
          updateRowsStatus={updateRowsStatus}
          originalData={data}
          predicate={predicate}
          dataConfig={dataConfig}
        />
      </Box>
      <BaseTable
        // Remounted when the tank answer changes so `initialState` below can
        // show or hide the tank columns. It settles as the routes and tanks
        // load, before anyone edits; afterwards it flips only on a save that
        // adds the first maturation figure, when nothing is unsaved.
        key={showTankColumns ? 'with-tanks' : 'no-tanks'}
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={() => {}} // Upload handled by external DropZone above
        isUploading={false}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerPackagingRoutes"
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        isCellEditable={tanks.size > 0 ? isCellEditable : undefined}
        // Remember each planner's column choices, widths, order and pinning.
        layoutKey="PackagingRoutes"
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
          // Hidden by default: this is the widest grid in the app and the four
          // PR Factor columns squeezed every other column until its values
          // were cut off. They stay in the Columns panel, in upload and in
          // download (BaseTable writes every upload column to the CSV, hidden
          // or not) — only the default view drops them. A planner who shows
          // them keeps that choice: the layout is saved per user.
          columns: {
            columnVisibilityModel: {
              pr1: false,
              pr2: false,
              pr3: false,
              pr4: false,
              // Hidden by default too (Andy, 2026-10-30). Still in the
              // Columns selector, upload and download.
              packingLine1: false,
              packingLine2: false,
              packingLine3: false,
              packingLine4: false,
              // Offered in the Columns selector but hidden on a plant with
              // no tank, where they would be empty.
              ...(!showTankColumns &&
                Object.fromEntries(TANK_FIELDS.map((f) => [f, false]))),
            },
          },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          // A Kitchen / Line cell that names a declared tank.
          '& .tank-cell': {
            fontWeight: 600,
            color: '#0E7490',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
      />
    </Box>
  )
}
